/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { AccountResponseSchemaKey } from './responseSchemaKeys';

export type MismatchSeverity =
  /** The payload itself is the wrong shape — wrong type at the root, or null. */
  | 'response-shape'
  /** The payload is broadly right; named fields drifted. */
  | 'field-drift';

/**
 * Scalars and strings only. Whatever reaches a logger gets serialised, and a
 * zod error or an AxiosResponse there produces the multi-MB log lines
 * documented in `movetix/src/lib/api/logger.ts`.
 */
export interface SchemaMismatch {
  key: AccountResponseSchemaKey;
  /** Templatized, e.g. `GET /um/v2/users/{id}/travel-doc`. Never a real path. */
  endpoint: string;
  severity: MismatchSeverity;
  /** `path: expected X, got Y` per issue — type names only, never values. */
  issues: string[];
  issueCount: number;
  /** How many times this exact mismatch has been seen this session. */
  occurrences: number;
}

export type SchemaMismatchReporter = (mismatch: SchemaMismatch) => void;

/**
 * The services are plain classes and the logger is React context, so it is
 * injected here rather than imported — same seam as
 * `movetix/src/lib/api/logger.ts`. `useSchemaReporter` installs it.
 *
 * A set rather than one slot: every `useApiQuery`/`useApiMutation` installs its
 * own, so a screen usually holds several at once.
 */
const reporters = new Set<SchemaMismatchReporter>();

export const setSchemaReporter = (report: SchemaMismatchReporter): void => {
  reporters.add(report);
};

/** Drops only `report`, so one unmount cannot unhook the trees still mounted. */
export const clearSchemaReporter = (report?: SchemaMismatchReporter): void => {
  if (report) reporters.delete(report);
  else reporters.clear();
};

/** Until a reporter is installed, and in tests and scripts where none ever is. */
const reportToConsole = (mismatch: SchemaMismatch): void => {
  if (typeof __DEV__ !== 'undefined' && __DEV__) {
    console.warn(
      `[account] response schema mismatch — ${mismatch.endpoint} ` +
        `[${mismatch.key}/${mismatch.severity}] ${mismatch.issueCount} issue(s), ` +
        `seen ${mismatch.occurrences}x: ${mismatch.issues.join('; ')}`,
    );
  }
};

/** Reports once, through the newest reporter — they all reach the same logger. */
export const reportSchemaMismatch = (mismatch: SchemaMismatch): void => {
  const [active] = Array.from(reporters).slice(-1);

  (active ?? reportToConsole)(mismatch);
};
